import { format, parse } from "date-fns";
import { GEO_DATE_FILTER_OPTIONS } from "../constants";
import { fetchDataV2, newApiFetchV2, postDataV2 } from "./utils";

export const getParams = () => {
  const params = window.location.search;
  const parameters = new URLSearchParams(params);

  const dateRangeParam = parameters.get("date_range");
  // Trim before comparing to handle both ALL_OPTION ("All ") and ALL_OPTION_NO_SPACE ("All"),
  // which can end up in the URL depending on which Redux initial state was in effect on first render.
  const getParam = (key: string) => parameters.get(key)?.trim() ?? "";
  const splitParam = (key: string) =>
    getParam(key)
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  const isAll = (key: string) => {
    const val = getParam(key);
    return !val || val.startsWith("All") || val === "undefined";
  };

  const market = isAll("market") ? [] : splitParam("market");
  const promptType = isAll("prompt_type") ? [] : splitParam("prompt_type");
  const strategicTheme = isAll("strategic_theme") ? [] : splitParam("strategic_theme");
  const domain = isAll("domain") ? [] : splitParam("domain");
  const funnel = isAll("funnel") ? [] : splitParam("funnel");

  const date = dateRangeParam
    ? GEO_DATE_FILTER_OPTIONS.includes(dateRangeParam)
      ? dateRangeParam.split(" ").join("_").toLocaleLowerCase()
      : "custom"
    : "";

  const parameterEntries: Record<string, string | null | Array<string> | undefined> = {
    date_range: date,
    market: market,
    prompt_type: promptType,
    strategic_theme: strategicTheme,
    domain: domain,
    funnel: funnel,
  };

  if (date === "custom" && dateRangeParam) {
    const now = new Date();
    const [startRaw, endRaw] = dateRangeParam.split("-");
    parameterEntries["start_date"] = format(parse(startRaw.trim(), "dd/MM/yyyy", now), "y-MM-dd");
    parameterEntries["end_date"] = format(parse(endRaw.trim(), "dd/MM/yyyy", now), "y-MM-dd");
  }

  return parameterEntries;
};

// Ensures React Query never receives undefined (e.g. on cancelled requests or 499s)
const geoFetch = (endpoint: string) => newApiFetchV2(endpoint, getParams()).then((r: any) => r ?? null);

export const getGeoReportingFilters = () => geoFetch(`/geo-reporting/filters`);

export const getGeoReportingLastRefreshDate = () => geoFetch(`/geo-reporting/last-refresh-date`);

export const getGeoReportingOverviewSummary = () => geoFetch(`/geo-reporting/get-overview-summary`);

export const getMarketOverview = () => geoFetch(`/geo-reporting/market-overview`);

export const getGeoReportingStrategicThemesOverview = () => geoFetch(`/geo-reporting/strategic-themes-overview`);

export const getGeoReportingNissanVsCompetitorsOverview = () => geoFetch(`/geo-reporting/nissan-vs-competitors-overview`);

export const getEvolutionOverTime = () => geoFetch(`/geo-reporting/evolution-over-time`);

export const getEvolutionOverTimeByDomain = () => geoFetch(`/geo-reporting/evolution-over-time-by-domain`);

export const getOverviewOfSourceDomains = () => geoFetch(`/geo-reporting/overview-of-source-domains`);

export const getOverviewOfPromptPerformance = () => geoFetch(`/geo-reporting/overview-of-prompt-performance`);

export const getDomainList = () => geoFetch(`/geo-reporting/domain-list`);

export const getFunnelList = () => geoFetch(`/geo-reporting/funnel-list`);

export const getDataDefinitions = () => fetchDataV2(`/geo-reporting/get-data-definitions`);

// Restored - the live _dashboard.key for this page's route (/seo_dashboard/geo_reporting) is
// "poi_seo_dashboard_geo_reporting" (confirmed against a real GET /dashboards response -
// dashboardRegistry.ts's own comment), but ANALYSIS_REGISTRY (backend) is keyed by the literal
// string "geo_reporting" with no fallback/fuzzy match (services/analysis/service.py's own
// dashboard.key lookup) - so exporting through the generic endpoint 404s regardless of which real
// dashboard_id gets resolved. These 7 bespoke endpoints (of the original 9 - 2 aren't wired to any
// button in the current UI) are confirmed still live/functional.
const geoCsvDownload = (endpoint: string) => postDataV2(`/geo-reporting/${endpoint}/csv-download`, getParams());

export const downloadMarketOverviewCsv = () => geoCsvDownload("market-overview");
export const downloadStrategicThemesCsv = () => geoCsvDownload("strategic-themes-overview");
export const downloadNissanVsCompetitorsOverviewCsv = () => geoCsvDownload("nissan-vs-competitors-overview");
export const downloadEvolutionOverTimeCsv = () => geoCsvDownload("evolution-over-time");
export const downloadSourceDomainsOverviewCsv = () => geoCsvDownload("overview-of-source-domains");
export const downloadEvolutionOverTimeByDomainCsv = () => geoCsvDownload("evolution-over-time-by-domain");
export const downloadPromptPerformanceCsv = () => geoCsvDownload("overview-of-prompt-performance");
